'use client'

import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Markdown } from 'tiptap-markdown'
import type { MarkdownStorage } from 'tiptap-markdown'
import { useEffect, useState } from 'react'
import { LinkDialog } from './LinkDialog'

type Props = {
  value: string
  onChange: (markdown: string) => void
  placeholder?: string
  dark?: boolean
}

type ToolbarButtonProps = {
  onClick: () => void
  active?: boolean
  label: string
  title: string
  dark?: boolean
}

type MarkdownEditorStorage = {
  markdown: MarkdownStorage
}

// text-base auf Mobil: unter 16px zoomt iOS Safari beim Fokus automatisch hinein.
export const MARKDOWN_EDITOR_CONTENT_CLASS =
  // Hier bewusst keine Fokus-Unterdrückung: die Tastaturanzeige ist in
  // globals.css ein inset box-shadow auf :focus-visible. Eine Klasse wie
  // focus:shadow-none nähme dem Editor jede Fokusanzeige. Der Maus-Fokus ist
  // dort bereits global über :focus:not(:focus-visible) abgeräumt.
  'markdown-editor-content min-h-[120px] p-4 lg:p-6 pb-9 font-sans text-base md:text-sm leading-relaxed tracking-normal'

export const MARKDOWN_EDITOR_PLACEHOLDER_CLASS =
  'absolute top-4 lg:top-6 left-4 lg:left-6 font-sans text-base md:text-sm leading-relaxed tracking-normal pointer-events-none select-none'

export const MARKDOWN_EDITOR_STARTER_KIT_OPTIONS: Partial<StarterKitOptions> = {
  heading: { levels: [2, 3] },
  code: false,
  codeBlock: false,
  link: false,
}

function getMarkdownFromEditor(editor: { storage: unknown }) {
  return (editor.storage as MarkdownEditorStorage).markdown.getMarkdown()
}

function ToolbarButton({ onClick, active, label, title, dark }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active ?? false}
      // mousedown hält nur die Selektion im Editor, ausgelöst wird per click —
      // sonst wäre die Toolbar für Tastaturnutzer wirkungslos.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`min-h-11 min-w-11 inline-flex items-center justify-center px-2 py-1 font-mono text-[10px] font-bold uppercase transition-colors cursor-pointer ${
        dark
          ? active ? 'bg-paper text-ink' : 'text-paper/50 hover:text-paper hover:bg-paper/10'
          : active ? 'bg-ink text-paper' : 'text-ink/50 hover:text-ink hover:bg-ink/10'
      }`}
    >
      {label}
    </button>
  )
}

export function MarkdownEditor({ value, onChange, placeholder, dark }: Props) {
  const [isEmpty, setIsEmpty] = useState(true)
  const [mdContent, setMdContent] = useState('')
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure(MARKDOWN_EDITOR_STARTER_KIT_OPTIONS),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
      }),
    ],
    immediatelyRender: false,
    content: value ? value : '',
    editorProps: {
      attributes: {
        class: `${MARKDOWN_EDITOR_CONTENT_CLASS} ${dark ? 'text-paper' : 'text-ink'}`,
      },
    },
    onUpdate({ editor }) {
      const md = getMarkdownFromEditor(editor)
      onChange(md)
      setMdContent(md)
      setIsEmpty(editor.isEmpty)
    },
  })

  // Sync external value changes (e.g. form reset)
  useEffect(() => {
    if (!editor) return
    const current = getMarkdownFromEditor(editor)
    if (current !== value) {
      editor.commands.setContent(value ?? '')
    }
  }, [value, editor])

  if (!editor) return null

  const charCount = mdContent.replace(/[*#_\[\]`>\-]/g, '').length

  const handleLinkButton = () => {
    setLinkDialogOpen(true)
  }

  const handleLinkConfirm = (url: string) => {
    editor.chain().focus().setLink({ href: url }).run()
    setLinkDialogOpen(false)
  }

  const handleLinkRemove = () => {
    editor.chain().focus().unsetLink().run()
    setLinkDialogOpen(false)
  }

  return (
    <>
      <div className="relative">
        {/* Toolbar */}
        <div className={`flex flex-wrap gap-0.5 px-3 pt-3 border-b ${dark ? 'border-paper/20' : 'border-ink/20'}`}>
          <ToolbarButton dark={dark} label="B" title="Fett (⌘B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
          <ToolbarButton dark={dark} label="I" title="Kursiv (⌘I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
          <span className={`w-px mx-1 self-stretch ${dark ? 'bg-paper/20' : 'bg-ink/20'}`} />
          <ToolbarButton dark={dark} label="H2" title="Überschrift groß" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
          <ToolbarButton dark={dark} label="H3" title="Überschrift klein" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
          <span className={`w-px mx-1 self-stretch ${dark ? 'bg-paper/20' : 'bg-ink/20'}`} />
          <ToolbarButton dark={dark} label="• Liste" title="Aufzählung" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
          <ToolbarButton dark={dark} label="1. Liste" title="Nummerierte Liste" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
          <span className={`w-px mx-1 self-stretch ${dark ? 'bg-paper/20' : 'bg-ink/20'}`} />
          <ToolbarButton dark={dark} label="❝" title="Zitat" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
          <span className={`w-px mx-1 self-stretch ${dark ? 'bg-paper/20' : 'bg-ink/20'}`} />
          <ToolbarButton dark={dark} label="Link" title={editor.isActive('link') ? 'Link bearbeiten' : 'Link einfügen'} active={editor.isActive('link')} onClick={handleLinkButton} />
        </div>

        {/* Editor area */}
        <div className="relative">
          {isEmpty && (
            <p className={`${MARKDOWN_EDITOR_PLACEHOLDER_CLASS} ${dark ? 'text-paper/30' : 'text-ink/30'}`}>
              {placeholder ?? 'Beschreibung hier eingeben...'}
            </p>
          )}
          <EditorContent editor={editor} />
        </div>

        {/* Zeichenzähler absolut über der Unterpolsterung des Textbereichs:
            so reicht der fokussierbare Bereich bis an die Unterkante der Zelle
            und die Fokuslinie sitzt auf der sichtbaren Kante. */}
        <div
          className={`absolute inset-x-0 bottom-0 px-4 pb-2 font-mono text-[10px] uppercase flex justify-between ${
            charCount > 600
              ? dark ? 'text-orange-300' : 'text-orange-700'
              : charCount > 300
                ? dark ? 'text-yellow-200' : 'text-yellow-800'
                : 'opacity-40'
          }`}
        >
          <span>
            {charCount > 600
              ? 'Sehr langer Text — lieber kürzer fassen.'
              : charCount > 300
                ? 'Lange Texte werden selten vollständig gelesen.'
                : ''}
          </span>
          <span>{charCount} Zeichen</span>
        </div>
      </div>

      <LinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onConfirm={handleLinkConfirm}
        onRemove={editor.isActive('link') ? handleLinkRemove : undefined}
        initialUrl={editor.getAttributes('link').href ?? ''}
      />
    </>
  )
}
